/**
 * src/components/primitives/AccessGate.tsx — honest disable-and-tooltip gate
 * (FR-008 + FR-010).
 *
 * Wired primitive that consumes the AUTHORITATIVE server role from
 * `useCurrentMember()` (the `members` row via Convex; PRD FR-004, D-003)
 * and consults the client permission mirror at `src/lib/permissions.ts`
 * to decide whether to ENABLE or DISABLE the wrapped affordance.
 *
 * The three documented states (CLAUDE.md §5):
 *
 *   1) RESOLVING (`useCurrentMember() === undefined`) — the member
 *      subscription is still loading. Render a `<Skeleton>` shaped like a
 *      privileged-action chip; NEVER render the underlying children in
 *      either enabled or disabled form. Rendering either optimistically
 *      lies about the operator's authority during the few-hundred-ms
 *      window before Convex pushes the row.
 *
 *   2) ALLOWED (`can(perm, member.role)` is true) — render the children
 *      verbatim, no wrapper-imposed visual change, no `pointer-events:
 *      none`. The server is still the real gate — the mutation wrapper
 *      will re-check on submit.
 *
 *   3) INSUFFICIENT (signed out OR `can(perm, role)` is false) — render
 *      the children inside a wrapper that:
 *        - keeps the children in the DOM (DISABLE-not-HIDE — the operator
 *          sees the affordance exists, just out of reach);
 *        - carries an accessible tooltip (`title` + `aria-label` +
 *          `aria-describedby` → an off-screen description) naming the
 *          missing permission;
 *        - applies the disable visual contract (opacity, cursor:
 *          not-allowed, pointer-events: none) so real browsers block the
 *          click even though jsdom may still synthesise an event for it.
 *
 * RED-on-regression: hiding the children, optimistically enabling on
 * insufficient role, or rendering the underlying control while resolving,
 * fails `tests/primitives/access-gate.test.ts` (T309 → T310).
 *
 * Server-is-real-gate invariant: this primitive only decides UX. Every
 * mutation re-checks `requirePermission()` server-side (see
 * `convex/lib/permissions.ts:80-100`).
 */
import React, { type CSSProperties, type ReactNode, useId } from 'react';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { can, type Permission } from '../../lib/permissions';
import { Skeleton } from './Skeleton';

interface AccessGateProps {
  /** Permission key from the client mirror. Phantom keys evaluate to `can() === false`. */
  perm: Permission | string;
  /** The privileged affordance. Stays in the DOM in every state except RESOLVING. */
  children: ReactNode;
  /**
   * Optional fallback to substitute for the wrapped children in the
   * INSUFFICIENT state. Provided for backwards compatibility with the
   * prototype shape (`primitives/index.tsx:356-363`). Most call sites omit
   * it — the default disable-and-tooltip wrapper is the canonical UX.
   */
  fallback?: ReactNode;
}

/**
 * Honest-state copy. The tooltip text is operator-language (Spanish — the
 * v.3 prototype's shipping language; see CLAUDE.md §12 and the FR-018
 * glossary gate). The permission name appears verbatim so power-users can
 * cross-reference the role/permission matrix on the help page.
 */
function tooltipFor(perm: string): string {
  return `Necesitas el permiso "${perm}" para esta acción.`;
}

export function AccessGate({
  perm,
  children,
  fallback,
}: AccessGateProps): React.ReactElement {
  const member = useCurrentMember();
  const tooltipId = useId();

  // State 1 — RESOLVING. Render a Skeleton; never leak the underlying
  // control. Width matches a typical button affordance.
  if (member === undefined) {
    return (
      <Skeleton w={120} h={32} r={16} aria-busy="true" />
    );
  }

  // Decide allowance against the client mirror. `null` member (signed
  // out) and any role NOT in the allow-list both collapse to `false`.
  const allowed = member !== null && can(perm, member.role);

  if (allowed) {
    // State 2 — ALLOWED. Render children verbatim; no wrapper attributes
    // that would affect their interactive surface.
    return <>{children}</>;
  }

  // State 3 — INSUFFICIENT. Allow callers to substitute a fallback (the
  // prototype shape); otherwise render the disable-and-tooltip wrapper.
  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  const tip = tooltipFor(typeof perm === 'string' ? perm : String(perm));
  const wrapperStyle: CSSProperties = {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    display: 'inline-flex',
  };

  return (
    <span
      title={tip}
      aria-label={tip}
      aria-describedby={tooltipId}
      role="group"
      style={wrapperStyle}
      data-circa-access-gate="disabled"
    >
      {/* off-screen description for assistive tech */}
      <span
        id={tooltipId}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {tip}
      </span>
      {children}
    </span>
  );
}

export default AccessGate;
